import { autoinject } from 'aurelia-framework';
import { NetlogHttpClient } from 'lib/http';
import { QueryFilter } from 'lib/tables/DataFilter';
import { CreateEntityNoteDto, EntityNoteDto, UpdateEntityNoteDto } from 'models';
import { CreatedEntity } from 'models/BaseEntity';
import {
  CreateMooringOfferDto,
  MooringOfferDto,
  MooringOfferListItemDto,
  UpdateMooringOfferDto,
  CompleteReviewDto,
  UpdateReviewDto,
  MooringReviewDto,
  AbortReviewDto,
  UpdateMooringOfferStatusDto,
  TransferToAquacomDto,
  UpdateInAquacomDto,
  MooringOfferStatusPipelineItem,
  UpdateMooringOfferStatusContentQueueDto,
  UpdateMooringOfferReviewStepDto,
  MooringOfferVersionDto,
  MooringOfferBomDto,
  CreateMooringExtraArticleDto,
  UpdateMooringExtraArticleDto,
  MooringOfferChangeDifferenceDto,
  MooringOfferChangeImpactDto,
} from 'models/mooring';

@autoinject
export class MooringOfferService {
  constructor(private client: NetlogHttpClient) {}

  async getOffer(id: number) {
    return await this.client.get<MooringOfferDto>({
      url: `api/mooring-offers/${id}`,
      model: MooringOfferDto,
    });
  }

  async getOffers(query: QueryFilter) {
    return await this.client.get<MooringOfferListItemDto[]>({
      url: `api/mooring-offers${query.asQueryString}`,
      model: MooringOfferListItemDto,
    });
  }

  async createOffer(data: CreateMooringOfferDto) {
    return await this.client.post({
      data,
      url: 'api/mooring-offers',
      model: CreatedEntity,
    });
  }

  async updateOffer(id: number, data: UpdateMooringOfferDto) {
    return await this.client.patch({
      data,
      url: `api/mooring-offers/${id}`,
    });
  }

  async copyOffer(id: number) {
    return await this.client.post({ url: `api/mooring-offers/copy/${id}` });
  }

  async previewOfferChanges(id: number, data: MooringOfferChangeDifferenceDto) {
    return await this.client.post<MooringOfferChangeImpactDto>({
      data,
      url: `api/mooring-offers/${id}/previewChanges`,
      model: MooringOfferChangeImpactDto,
    });
  }

  async updateOfferStatus(id: number, data: UpdateMooringOfferStatusDto) {
    return await this.client.patch({
      data,
      url: `api/mooring-offers/${id}/status`,
    });
  }

  async deleteOffer(id: number) {
    return await this.client.delete({ url: `api/mooring-offers/${id}` });
  }

  async getBoardData() {
    return await this.client.get<MooringOfferStatusPipelineItem[]>({
      url: `api/mooring-offers/boardData`,
      model: MooringOfferStatusPipelineItem,
    });
  }

  async updateBoardDataPipelineContent(statusId: number, data: UpdateMooringOfferStatusContentQueueDto) {
    return await this.client.patch({
      url: `api/mooring-offers/boardData/${statusId}/content`,
      data,
    });
  }

  async transferOfferToAquacom(transferToAquacom: TransferToAquacomDto) {
    return await this.client.post({
      url: `api/mooring-offers/${transferToAquacom.OfferId}/transferToAquacom/customer/${transferToAquacom.CustomerId}/locality/${transferToAquacom.LocalityId}`,
      data: {},
    });
  }

  async updateOfferInAquacom(updateInAquacom: UpdateInAquacomDto) {
    return await this.client.post({
      url: `api/mooring-offers/${updateInAquacom.OfferId}/updateMooringOfferInAquacom/customer/${updateInAquacom.CustomerId}/locality/${updateInAquacom.LocalityId}/mooringAquacomId/${updateInAquacom.MooringAquacomId}`,
      data: {},
    });
  }

  async exportOfferNavision(offerId: number) {
    return await this.client.downloadBlob(`api/mooring-offers/${offerId}/export`);
  }

  async transferToFileArea(offerId: number) {
    return await this.client.post({
      url: `api/mooring-offers/${offerId}/transferBOMToFileArea`,
      data: {},
    });
  }

  // Review
  async sendForReview(offerId: number) {
    return await this.client.post({
      url: `api/mooring-offer-reviews/offers/${offerId}`,
      data: {},
    });
  }

  async unlockOffer(offerId: number) {
    return await this.client.post<{ Id: number }>({
      url: `api/mooring-offers/${offerId}/unlock`,
      data: {},
      model: CreatedEntity,
    });
  }

  async getOfferReviews(offerId: number) {
    return await this.client.get<MooringReviewDto[]>({
      url: `api/mooring-offer-reviews/offers/${offerId}`,
      model: MooringReviewDto,
    });
  }

  async getReview(reviewId: number) {
    return await this.client.get<MooringReviewDto>({
      url: `api/mooring-offer-reviews/${reviewId}`,
      model: MooringReviewDto,
    });
  }

  async completeReview(reviewId: number, dto: CompleteReviewDto) {
    return await this.client.post({
      url: `api/mooring-offer-reviews/${reviewId}/complete`,
      data: dto,
    });
  }

  async abortReview(reviewId: number, dto: AbortReviewDto) {
    return await this.client.post({
      url: `api/mooring-offer-reviews/${reviewId}/abort`,
      data: dto,
    });
  }

  async updateReview(reviewId: number, dto: UpdateReviewDto) {
    return await this.client.patch({
      url: `api/mooring-offer-reviews/${reviewId}`,
      data: dto,
    });
  }

  async updateReviewStep(reviewId: number, dto: UpdateMooringOfferReviewStepDto) {
    return await this.client.patch({
      url: `api/mooring-offer-reviews/${reviewId}/step`,
      data: dto,
    });
  }

  async createNote(offerId: number, dto: CreateEntityNoteDto) {
    return await this.client.post({
      url: `api/mooring-offers/${offerId}/notes`,
      data: dto,
    });
  }

  async updateNote(offerId: number, noteId: number, dto: UpdateEntityNoteDto) {
    return await this.client.patch({
      url: `api/mooring-offers/${offerId}/notes/${noteId}`,
      data: dto,
    });
  }

  async deleteNote(offerId: number, noteId: number) {
    return await this.client.delete({
      url: `api/mooring-offers/${offerId}/notes/${noteId}`,
    });
  }

  async getNotes(offerId: number) {
    return await this.client.get<EntityNoteDto[]>({
      url: `api/mooring-offers/${offerId}/notes`,
      model: EntityNoteDto,
    });
  }

  async getVersions(offerId: number) {
    return await this.client.get<MooringOfferVersionDto[]>({
      url: `api/mooring-offers/${offerId}/versions`,
      model: MooringOfferVersionDto,
    });
  }

  async getVersion(offerId: number, versionId: number) {
    return await this.client.get<MooringOfferVersionDto>({
      url: `api/mooring-offers/${offerId}/versions/${versionId}`,
      model: MooringOfferVersionDto,
    });
  }

  // BOM
  async getBom(offerId: number, groupByCategory = false) {
    return await this.client.get<MooringOfferBomDto>({
      url: `api/mooring-offers/${offerId}/bom?groupByCategory=${groupByCategory}`,
      model: MooringOfferBomDto,
    });
  }

  async addExtraArticle(mooringId: number, data: CreateMooringExtraArticleDto) {
    return await this.client.post<{ Id: number }>({
      url: `api/mooring-offers/${mooringId}/extra-articles`,
      data,
    });
  }

  async updateExtraArticle(extraArticleId: number, data: UpdateMooringExtraArticleDto) {
    return await this.client.patch({
      url: `api/mooring-offers/extra-articles/${extraArticleId}`,
      data,
    });
  }

  async deleteExtraArticle(extraArticleId: number) {
    return await this.client.delete({
      url: `api/mooring-offers/extra-articles/${extraArticleId}`,
    });
  }
}
